/**
 * forwardResumeBreadcrumbText — the words of the forward-resume breadcrumb, chosen by
 * what the person actually did with the card.
 *
 * The breadcrumb used to say "The connection for the earlier X request is now
 * available" for every kind. That sentence is written for composio_auth. Live task
 * 28edef30 (2026-09-28) wrote it after a stale APPROVAL card, so the model read that a
 * connection it never asked for had arrived. A declined card was worse: "Continue that
 * request" invited the very step the person refused. So:
 *   - a decline of any kind: the step was declined, do not run it;
 *   - tool_permission allow: "The person approved the earlier X step." (the breadcrumb
 *     sits in a user-role slot, so "You approved" could read as the model approving);
 *   - composio_auth allow: the connection sentence, unchanged;
 *   - a purchase card's answer (a custom allow on buy carrying buy's result, the agent
 *     wallet): the answer is NOT lost. Its message, exactly as the server wrote it, and its
 *     purchase id are carried forward after every other sentence, so buy_report can still
 *     close the purchase;
 *   - any other kind: the person answered, but the answer is NOT here. The deferred slot
 *     is immutable and the outcome's content is dropped (journal.ts reconcileInteractions,
 *     the forward-resume branch), so "was answered" would promise the model an answer it
 *     never gets. It says so, and to ask again if it still needs it.
 * Model-facing text: no em dashes (the model echoes them into user-facing replies).
 */

import type { InteractionKind, InteractionOutcome } from '../../contract/interaction.js';
import { BUY_TOOL } from '../../policy/side-effect-classifier.js';

/** What the breadcrumb needs from each resolved pause (a ForwardResumeDirective fits). */
export interface ResolvedPause {
  kind: InteractionKind;
  toolName: string;
  outcome: InteractionOutcome;
}

type Lead = 'declined' | 'approved' | 'connected' | 'answered' | 'asked';

const LEADS: Record<Lead, (named: string) => string> = {
  declined: (named) => `The ${named} request was declined, so do not run it.`,
  approved: (named) => `The person approved the ${named} step.`,
  connected: (named) => `The connection for the ${named} request is now available.`,
  answered: (named) =>
    `The person answered the ${named} request after you had moved on; that answer is not in this conversation. If you still need it, ask again.`,
  asked: (named) =>
    `The person answered the ${named} question after you had moved on; that answer is not in this conversation. If you still need it, ask again.`,
};

function leadOf(p: ResolvedPause): Lead {
  if (p.outcome.behavior === 'deny') return 'declined';
  if (p.kind === 'tool_permission') return 'approved';
  if (p.kind === 'composio_auth') return 'connected';
  return p.kind === 'ask_question' ? 'asked' : 'answered';
}

/**
 * buy's result statuses (@bash/shared BuyToolStatus). agent-sdk does not depend on @bash/shared, so it keeps its own
 * copy; apps/sandbox, which depends on both, holds it equal to shared's BUY_TOOL_STATUSES
 * (wallet/__tests__/buy-statuses-agree.test.ts).
 */
export const BUY_RESULT_STATUSES: readonly string[] = ['pay_step_done', 'declined', 'expired'];

/**
 * A purchase card's answer (the agent wallet, spec 2026-10-15; contract C5): buy's own result, carried forward so the
 * model still gets pay_step_done and the purchase id buy_report needs when its slot was deferred. agent-sdk does not
 * depend on @bash/shared, so the shape is read here by its fields (apps/sandbox reads the same shape through
 * @bash/shared buyToolResultOf). Only buy's own card is read this way, so a buy-shaped answer on another custom card
 * keeps the old wording. The message is passed through as the server wrote it (a timed-out pay step has its own).
 */
function purchaseAnswerOf(p: ResolvedPause): string | null {
  if (p.kind !== 'custom' || p.toolName !== BUY_TOOL || p.outcome.behavior !== 'allow') return null;
  const v = (p.outcome as { updatedInput?: unknown }).updatedInput;
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const { status, purchaseId, message } = v as { status?: unknown; purchaseId?: unknown; message?: unknown };
  if (typeof purchaseId !== 'string' || purchaseId.length === 0 || typeof message !== 'string') return null;
  if (typeof status !== 'string' || !BUY_RESULT_STATUSES.includes(status)) return null;
  return `The earlier ${p.toolName} request has its answer: ${message} The purchase_id is ${purchaseId}.`;
}

export function forwardResumeBreadcrumbText(resolved: readonly ResolvedPause[]): string {
  const purchases = resolved.map(purchaseAnswerOf).filter((s): s is string => s !== null);
  const rest = resolved.filter((p) => purchaseAnswerOf(p) === null);
  if (rest.length === 0) return `[System] ${purchases.join(' ')}`;
  // One sentence per lead, naming its tools in first-seen order (one combined entry).
  const names = new Map<Lead, string[]>();
  for (const p of rest) {
    const lead = leadOf(p);
    const list = names.get(lead) ?? [];
    if (p.toolName) list.push(p.toolName);
    names.set(lead, list);
  }
  const leads = [...names].map(([lead, tools]) => LEADS[lead](tools.length ? `earlier ${tools.join(', ')}` : 'earlier'));
  // Continue only what can run again; an answer that is not here already says what to do.
  const next = names.has('approved') || names.has('connected')
    ? ' Continue that request, taking into account everything discussed since.'
    : names.has('declined') ? ' Carry on, taking into account everything discussed since.' : '';
  return `[System] ${leads.join(' ')}${next}${purchases.length > 0 ? ` ${purchases.join(' ')}` : ''}`;
}
